import styled from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
import { Typography } from "@mui/material";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";

import CloseIcon from "@mui/icons-material/Close";

import font from "src/styles/font";
import { Black, DarkGrey, Primary, White01 } from "src/styles/color";

import Button from "src/components/core/Button";
import device from "src/styles/device";

const Dialog = muiStyled(MuiDialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
    position: "relative",
  },
  "& .MuiDialogTitle-root": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  "& .MuiDialogContent-root": {
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 870,
    },
  },
  "& .MuiDialogActions-root": {
    padding: 16,
    borderTop: `1px solid ${White01}`,
    justifyContent: "flex-end",
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    color: ${Black};
  }
`;

const FontHead = styled.div`
  font-size: 1.125rem;
  color: ${Primary};
  font-weight: 600;
`;

const TextDesc = styled.div`
  margin: 8px 0 12px;
  text-align: justify;
  text-justify: inter-word;
`;

const FontP = styled.p`
  margin: 0;
  font-size: ${font.size.md};
  font-weight: 400;
  line-height: 1.7;
  color: ${(props) => (props.fontColor ? props.fontColor : Black)};

  span {
    font-size: ${font.size.md};
    font-weight: 600;
    line-height: 1.7;
    color: ${(props) => (props.fontColor ? props.fontColor : Black)};
  }

  &.text-indent {
    text-indent: 24px;

    @media ${device.mobileL} {
      text-indent: 16px;
    }
  }

  @media ${device.tablet} {
    font-size: ${font.size.sm} !important;
  }
`;

const UlBlock = styled.ul`
  margin-top: 5px;
  margin-bottom: 0px;
  padding-inline-start: 28px;

  > li {
    margin: 4px 0;
    font-size: ${font.size.md};
    font-weight: 400;
    color: Black;

    span {
      font-size: ${font.size.md};
      font-weight: 600;
      line-height: 1.7;
      color: Black;
    }

    @media ${device.tablet} {
      font-size: ${font.size.sm};
    }
  }
`;

const TermsPolicyDialog = ({ onClose, handleAccept }) => {
  return (
    <Dialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle sx={{ px: 2, py: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ color: Black }}>
          ข้อกำหนดและนโยบายการใช้งานระบบ XSURFACE AI
        </Typography>

        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" sx={{ color: DarkGrey }} />
        </CloseButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          width: "100%",
          "::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <FontHead>1. นโยบายการใช้งานและการคืนเงิน (Refund Policy)</FontHead>
        <TextDesc>
          <UlBlock>
            <li>
              <FontP>
                <span>การสั่งซื้อ:</span>{" "}
                เครดิตที่ท่านซื้อถือเป็นสิทธิ์การเข้าถึงบริการประมวลผล AI ของ
                XSURFACE ตามแพ็กเกจที่กำหนด
              </FontP>
            </li>
            <li>
              <FontP>
                <span>อายุการใช้งาน:</span>{" "}
                เครดิตมีระยะเวลาการใช้งานตามแพ็กเกจที่เลือก (30, 60 หรือ 90 วัน)
                นับจากวันที่ชำระเงิน
                เมื่อพ้นกำหนดเครดิตที่เหลือจะหมดอายุโดยอัตโนมัติและไม่สามารถแลกคืนเป็นเงินหรือสิทธิ์อื่นใดได้
              </FontP>
            </li>
            <li>
              <FontP>
                <span>นโยบายไม่คืนเงิน:</span>{" "}
                เนื่องจากเป็นบริการดิจิทัลที่ประมวลผลทันทีหลังการเติมเครดิตบริษัทขอสงวนสิทธิ์ไม่คืนเงินในทุกกรณีเมื่อเครดิตถูกเพิ่มเข้าบัญชีผู้ใช้แล้ว
              </FontP>
            </li>
            <li>
              <FontP>
                <span>การต่ออายุแพ็กเกจ:</span>{" "}
                ในกรณีที่ท่านชำระเงินแพ็กเกจใหม่ก่อนเครดิตเดิมหมดอายุ
                ระบบจะรวมยอดเครดิตเดิมและขยายระยะเวลาการใช้งานตามแพ็กเกจล่าสุดให้โดยอัตโนมัติ
              </FontP>
            </li>
          </UlBlock>
        </TextDesc>

        <FontHead>
          2. ลิขสิทธิ์และการใช้งาน (Copyright & Intellectual Property)
        </FontHead>
        <TextDesc>
          <UlBlock>
            <li>
              <FontP>
                <span>สิทธิ์ในการใช้งานรูปภาพ:</span>{" "}
                บริษัทมอบสิทธิ์ให้ผู้ใช้สามารถนำภาพที่สร้างโดย AI
                ไปใช้ได้ทั้งในเชิงพาณิชย์ การโฆษณาหรือการสื่อสารทางการตลาด
                โดยไม่มีค่าธรรมเนียมลิขสิทธิ์เพิ่มเติม
              </FontP>
            </li>
            <li>
              <FontP>
                <span>ความรับผิดชอบของผู้ใช้:</span>{" "}
                ผู้ใช้ต้องรับผิดชอบต่อรูปไฟล์ภาพต้นฉบับที่นำมาอัปโหลด
                และตกลงจะไม่ใช้บริการเพื่อสร้างเนื้อหาที่ผิดกฎหมาย ลามกอนาจาร
                หรือละเมิดลิขสิทธิ์ของบุคคลอื่น
              </FontP>
            </li>
            <li>
              <FontP>
                <span>ข้อจำกัดของเทคโนโลยี AI:</span>{" "}
                ภาพที่สร้างขึ้นผ่านระบบปัญญาประดิษฐ์อาจมีความคลาดเคลื่อน
                ด้านรายละเอียด
                บริษัทไม่รับประกันความถูกต้องหรือความสมบูรณ์ของผลลัพธ์ ในทุกกรณี
              </FontP>
            </li>
          </UlBlock>
        </TextDesc>

        <FontHead>3. นโยบายคุ้มครองข้อมูลส่วนบุคคล (PDPA)</FontHead>
        <TextDesc>
          <UlBlock>
            <li>
              <FontP>
                <span>ข้อมูลที่จัดเก็บ:</span>{" "}
                บริษัทจัดเก็บข้อมูลที่จำเป็นต่อการให้บริการ ได้แก่ ชื่อ-ที่อยู่,
                เลขผู้เสียภาษี (เพื่อออกใบกำกับภาษี), อีเมล,
                และประวัติการทำธุรกรรม
              </FontP>
            </li>
            <li>
              <FontP>
                <span>วัตถุประสงค์ในการใช้ข้อมูล:</span> เพื่อให้บริการประมวลผล
                AI, ออกเอกสารทางบัญชีตามข้อกำหนดของกฎหมาย
                และแจ้งเตือนสถานะการใช้งานแก่ผู้ใช้
              </FontP>
            </li>
            <li>
              <FontP>
                <span>ความปลอดภัยของข้อมูล:</span>{" "}
                ข้อมูลส่วนบุคคลและรูปภาพในประวัติการใช้งาน (History)
                จะถูกจัดเก็บด้วยการเข้ารหัสบนระบบ Cloud (AWS S3)
                ที่มีความปลอดภัยสูง
              </FontP>
            </li>
            <li>
              <FontP>
                <span>สิทธิของเจ้าของข้อมูล:</span> ท่านมีสิทธิ์ขอเข้าถึง แก้ไข
                หรือขอให้ลบข้อมูลส่วนบุคคลของท่านได้
                ภายใต้เงื่อนไขและขอบเขตที่กฎหมายกำหนด
              </FontP>
            </li>
          </UlBlock>
        </TextDesc>

        <FontHead>4. การออกเอกสารทางการเงิน</FontHead>
        <TextDesc>
          <FontP className="text-indent">
            บริษัทจะดำเนินการออกใบเสร็จรับเงิน/ใบกำกับภาษีเต็มรูปแบบให้แก่ผู้ใช้ภายในระยะเวลา
            2-5 วันทำการ นับจากวันที่ได้รับการยืนยันการชำระเงินเรียบร้อยแล้ว
            โดยผู้ใช้สามารถติดตามและดาวน์โหลดเอกสารผ่านระบบประวัติการใช้งาน
            (History) ได้ด้วยตนเอง
          </FontP>
        </TextDesc>

        <FontHead>5. การจัดการภาษีหัก ณ ที่จ่าย (สำหรับนิติบุคคล)</FontHead>
        <TextDesc>
          <UlBlock>
            <li>
              <FontP>
                <span>การแจ้งความประสงค์:</span>{" "}
                ลูกค้าที่สั่งซื้อในนามนิติบุคคลประสงค์หักภาษี ณ ที่จ่าย (อัตรา
                3%)
                ต้องกรอกเลขประจำตัวผู้เสียภาษีและที่อยู่ออกเอกสารให้ถูกต้องครบถ้วนในหน้า{" "}
                <span>"ข้อมูลการออกใบกำกับภาษี"</span> ก่อนดำเนินการชำระเงิน
              </FontP>
            </li>

            <li>
              <FontP>
                <span>ขั้นตอนการส่งเอกสาร:</span> ผู้ใช้ต้องอัปโหลดไฟล์{" "}
                <span>ใบรับรองการหัก ณ ที่จ่าย</span>{" "}
                ที่ลงนามและประทับตราสมบูรณ์ ผ่านช่องทางไลน์ออฟฟิเชียล @xsfseller
              </FontP>

              <UlBlock>
                <li>
                  บริษัทจะดำเนินการตรวจสอบเอกสารและพิจารณาคืนเงินส่วนภาษีหัก ณ
                  ที่จ่ายตามรอบบัญชี
                  ภายหลังจากได้รับเอกสารฉบับจริงครบถ้วนแล้วเท่านั้น
                </li>
              </UlBlock>
            </li>

            <li>
              <FontP>
                <span>ระยะเวลาดำเนินการ:</span>{" "}
                บริษัทจะดำเนินการตรวจสอบเอกสารและออกใบกำกับภาษีที่เกี่ยวข้องภายใน{" "}
                <span>7-14 วันทำการ</span> นับจากวันที่ได้รับเอกสารหัก ณ
                ที่จ่ายที่ถูกต้องและครบถ้วน
              </FontP>

              <UlBlock>
                <li>
                  หากเอกสารมีข้อมูลไม่ถูกต้องหรือข้อมูลไม่ครบถ้วน
                  ระยะเวลาดำเนินการจะเริ่มนับใหม่เมื่อได้รับเอกสารที่แก้ไขเรียบร้อยแล้ว
                </li>
              </UlBlock>
            </li>
            <li>
              <FontP>
                <span>การจัดส่งเอกสารฉบับจริง:</span>{" "}
                ผู้ใช้มีหน้าที่จัดส่งเอกสารใบรับรองการหัก ณ
                ที่จ่ายฉบับจริงมายังที่อยู่ของบริษัทภายในระยะเวลาที่กฎหมายกำหนด
                เพื่อให้การออกใบกำกับภาษีเป็นไปอย่างสมบูรณ์ตามข้อบังคับ
              </FontP>
            </li>
          </UlBlock>
        </TextDesc>
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          fullWidth
          onClick={() => handleAccept()}
          sx={{ maxWidth: 120 }}
        >
          รับทราบ
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsPolicyDialog;
